import { useEffect, useRef } from 'react';
import GenConfig from '../core/GenConfig';

/**
 * Progress fill that moves from click and respects solve phases.
 *
 * Bar space is proportional to expected time (pre / search / tail), not a
 * hardcoded 8%/85% split — otherwise short budgets look frozen in pre.
 *
 * Writes `style.width` through a ref so ticks do not re-render the owner.
 */

export const GAUGE_PHASE_PRE = 'pre';
export const GAUGE_PHASE_SEARCH = 'search';

const MAX_FILL = 0.995;
const TICK_MS = 100;

/** @param {string} status */
export function gaugePhaseFromStatus(status) {
    if (status === 'loading') return GAUGE_PHASE_SEARCH;
    return GAUGE_PHASE_PRE;
}

function easeTo(target, elapsedS, expectS) {
    const t = Math.max(0, elapsedS) / Math.max(expectS, 1e-6);
    return target * (1 - Math.exp(-3 * Math.min(t, 4)));
}

/**
 * @param {boolean} active
 * @param {number} timeLimit user-facing wall duration (seconds)
 * @param {'pre'|'search'} [phase]
 */
export default function useAsymptoticGauge(
    active,
    timeLimit,
    phase = GAUGE_PHASE_SEARCH,
    local = false,
) {
    const ref = useRef(null);
    const timingRef = useRef({
        runAt: 0,
        searchAt: 0,
        searchFloor: 0,
        lastFill: 0,
        preEnd: 0.2,
        searchEnd: 0.85,
        preExpectS: GenConfig.WARM_PRE_S,
        tailExpectS: GenConfig.WARM_TAIL_S,
        searchS: 5,
    });

    useEffect(() => {
        const el = ref.current;
        if (!el) return undefined;

        const bands = local
            ? GenConfig.localGaugeBands(timeLimit)
            : GenConfig.gaugeBands(timeLimit);

        if (!active) {
            el.style.width = '0%';
            timingRef.current = {
                runAt: 0,
                searchAt: 0,
                searchFloor: 0,
                lastFill: 0,
                preEnd: bands.preEnd,
                searchEnd: bands.searchEnd,
                preExpectS: bands.preExpectS,
                tailExpectS: bands.tailExpectS,
                searchS: bands.searchS,
            };
            return undefined;
        }

        const now = performance.now();
        const timing = timingRef.current;
        timing.preEnd = bands.preEnd;
        timing.searchEnd = bands.searchEnd;
        timing.preExpectS = bands.preExpectS;
        timing.tailExpectS = bands.tailExpectS;
        timing.searchS = bands.searchS;

        if (!timing.runAt) timing.runAt = now;

        if (phase === GAUGE_PHASE_SEARCH && !timing.searchAt) {
            timing.searchAt = now;
            timing.searchFloor = Math.max(bands.preEnd, timing.lastFill);
        }

        const budgetS = Math.max(bands.searchS, 1);

        const paint = () => {
            const t = performance.now();
            let fill = 0;
            const preEnd = timing.preEnd;
            const searchEnd = timing.searchEnd;
            // Soft extra room if wake/init overruns the expected pre budget.
            const preCap = Math.min(searchEnd - 0.02, preEnd + 0.06);

            if (phase === GAUGE_PHASE_PRE || !timing.searchAt) {
                const preElapsed = (t - timing.runAt) / 1000;
                if (preElapsed <= timing.preExpectS) {
                    // Linear with expected pre time so the bar keeps moving.
                    fill = preEnd * (preElapsed / Math.max(timing.preExpectS, 1e-6));
                } else {
                    const over = preElapsed - timing.preExpectS;
                    fill = preEnd + easeTo(preCap - preEnd, over, timing.preExpectS);
                }
                fill = Math.min(preCap, fill);
            } else {
                const searchElapsed = (t - timing.searchAt) / 1000;
                if (searchElapsed < budgetS) {
                    const u = Math.min(1, Math.max(0, searchElapsed / budgetS));
                    fill = timing.searchFloor + (searchEnd - timing.searchFloor) * u;
                } else {
                    const overS = searchElapsed - budgetS;
                    const headroom = MAX_FILL - searchEnd;
                    fill = searchEnd + easeTo(headroom, overS, timing.tailExpectS);
                }
            }

            fill = Math.min(MAX_FILL, Math.max(timing.lastFill, fill));
            timing.lastFill = fill;
            el.style.width = `${(fill * 100).toFixed(2)}%`;
        };

        paint();
        const id = setInterval(paint, TICK_MS);
        return () => clearInterval(id);
    }, [active, timeLimit, phase, local]);

    return ref;
}
